import React, { useCallback, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { ChevronLeftIcon, ChevronRightIcon, MinusIcon, PlusIcon, ScanIcon, XIcon } from 'lucide-react';
import { useZoomPan } from './useZoomPan';
export interface LightboxItem {
  src: string;
  alt: string;
  caption: string;
}
interface LightboxProps {
  items: LightboxItem[];
  /** Index of the open item, or null when closed. */
  index: number | null;
  onClose: () => void;
  onNavigate: (index: number) => void;
  label: string;
  counterNoun?: string;
}

/** Window event other UI (the sticky purchase bar) listens to. */
export const LIGHTBOX_EVENT = 'wpp-lightbox-toggle';
const iconButton = 'flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-white/10 text-white transition-colors duration-150 ease-out hover:bg-white/20 disabled:opacity-30 disabled:hover:bg-white/10 focus:outline-none focus-visible:ring-4 focus-visible:ring-arc/70';

/**
 * Full-screen viewer for original high-resolution pages.
 * Pinch / wheel / double-tap / buttons to zoom, drag to pan, swipe or arrow
 * keys to change image, Escape to close. Focus is trapped and restored.
 */
export function Lightbox({
  items,
  index,
  onClose,
  onNavigate,
  label,
  counterNoun = 'Image'
}: LightboxProps) {
  const open = index !== null;
  const current = index !== null ? items[index] : null;
  const count = items.length;
  const dialogRef = useRef<HTMLDivElement>(null);
  const closeRef = useRef<HTMLButtonElement>(null);
  const stageRef = useRef<HTMLDivElement>(null);
  const imageRef = useRef<HTMLImageElement>(null);
  const go = useCallback((direction: 1 | -1) => {
    if (index === null) return;
    const next = index + direction;
    if (next >= 0 && next < count) onNavigate(next);
  }, [index, count, onNavigate]);
  const zoom = useZoomPan(stageRef, imageRef, {
    onSwipe: go
  });
  const {
    reset
  } = zoom;
  useEffect(() => {
    reset();
  }, [index, reset]);
  useEffect(() => {
    if (!open) return;
    const previousFocus = document.activeElement as HTMLElement | null;
    // Scroll lock touches only the vertical axis of <html> (the real page
    // scroller) and remembers its exact previous inline value, so closing the
    // viewer never writes overflow:auto or re-enables horizontal scrolling.
    const root = document.documentElement;
    const previousRootOverflowY = root.style.getPropertyValue('overflow-y');
    const previousRootOverflowYPriority = root.style.getPropertyPriority('overflow-y');
    const scrollX = window.scrollX;
    const scrollY = window.scrollY;
    root.style.setProperty('overflow-y', 'hidden');
    window.dispatchEvent(new CustomEvent(LIGHTBOX_EVENT, {
      detail: true
    }));
    const frame = window.requestAnimationFrame(() => closeRef.current?.focus({
      preventScroll: true
    }));

    // While the viewer is open, pinch gestures belong to the image only — the
    // browser must not scale the landing page behind it.
    const blockPageZoom = (event: Event) => event.preventDefault();
    const blockMultiTouch = (event: TouchEvent) => {
      if (event.touches.length > 1) event.preventDefault();
    };
    const blockCtrlWheel = (event: WheelEvent) => {
      if (event.ctrlKey) event.preventDefault();
    };
    const listenerOptions: AddEventListenerOptions = {
      passive: false
    };
    // Safari (iOS / macOS) pinch events.
    document.addEventListener('gesturestart', blockPageZoom, listenerOptions);
    document.addEventListener('gesturechange', blockPageZoom, listenerOptions);
    document.addEventListener('gestureend', blockPageZoom, listenerOptions);
    // Multi-touch pinch in other mobile browsers.
    document.addEventListener('touchmove', blockMultiTouch, listenerOptions);
    // Trackpad pinch (reported as ctrl + wheel) on desktop.
    window.addEventListener('wheel', blockCtrlWheel, listenerOptions);
    return () => {
      window.cancelAnimationFrame(frame);
      document.removeEventListener('gesturestart', blockPageZoom);
      document.removeEventListener('gesturechange', blockPageZoom);
      document.removeEventListener('gestureend', blockPageZoom);
      document.removeEventListener('touchmove', blockMultiTouch);
      window.removeEventListener('wheel', blockCtrlWheel);
      if (previousRootOverflowY) {
        root.style.setProperty('overflow-y', previousRootOverflowY, previousRootOverflowYPriority);
      } else {
        root.style.removeProperty('overflow-y');
      }
      // Return the landing to exactly where the visitor left it.
      window.scrollTo({
        left: scrollX,
        top: scrollY,
        behavior: 'instant' as ScrollBehavior
      });
      window.dispatchEvent(new CustomEvent(LIGHTBOX_EVENT, {
        detail: false
      }));
      previousFocus?.focus?.({
        preventScroll: true
      });
    };
  }, [open]);
  const onKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    switch (event.key) {
      case 'Escape':
        event.preventDefault();
        onClose();
        break;
      case 'ArrowRight':
        event.preventDefault();
        go(1);
        break;
      case 'ArrowLeft':
        event.preventDefault();
        go(-1);
        break;
      case '+':
      case '=':
        zoom.zoomIn();
        break;
      case '-':
        zoom.zoomOut();
        break;
      case 'Tab':
        {
          const all = dialogRef.current?.querySelectorAll<HTMLElement>('button:not([disabled])');
          const nodes = Array.from(all ?? []).filter(node => node.getClientRects().length > 0 && getComputedStyle(node).visibility !== 'hidden');
          if (nodes.length === 0) return;
          const first = nodes[0];
          const last = nodes[nodes.length - 1];
          if (event.shiftKey && document.activeElement === first) {
            event.preventDefault();
            last.focus();
          } else if (!event.shiftKey && document.activeElement === last) {
            event.preventDefault();
            first.focus();
          }
          break;
        }
      default:
        break;
    }
  };
  if (typeof document === 'undefined') return null;
  return createPortal(<AnimatePresence data-id="element-86" data-mp="1dwbc0ubo2bmz">
      {open && current && index !== null && <motion.div ref={dialogRef} role="dialog" aria-modal="true" aria-label={`${label} — ${current.caption}`} onKeyDown={onKeyDown} initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} transition={{
      duration: 0.2,
      ease: [0.23, 1, 0.32, 1]
    }} className="fixed inset-0 z-50 flex touch-none flex-col overscroll-contain bg-charcoal-deep/[0.97] text-white" style={{
      paddingTop: 'env(safe-area-inset-top)',
      paddingBottom: 'env(safe-area-inset-bottom)'
    }} data-id="element-87" data-mp="1iummn79scq69">
          <div className="flex shrink-0 items-center justify-between gap-3 border-b border-white/10 px-4 py-2 sm:px-6" data-id="element-88" data-mp="2al0sqhbk4y7d">
            <div className="min-w-0" aria-live="polite" data-id="element-89" data-mp="c6pgvztsskix">
              <p className="label-tech text-arc-light" data-id="element-90" data-mp="g66n4vh1tlp5">
                {counterNoun} {index + 1} sur {count}
              </p>
              <p className="truncate font-heading text-[1.15rem] font-semibold uppercase leading-tight" data-id="element-91" data-mp="8o8pppc5612a">
                {current.caption}
              </p>
            </div>
            <button ref={closeRef} type="button" onClick={onClose} aria-label="Fermer la visionneuse" className={iconButton} data-id="element-92" data-mp="3v2a3kd7f8fv1">
              <XIcon className="h-5 w-5" aria-hidden data-id="element-93" data-mp="3r2hp5jvcjfhr" />
            </button>
          </div>

          <div ref={stageRef} {...zoom.handlers} className="relative min-h-0 flex-1 touch-none select-none overflow-hidden" style={{
        cursor: zoom.scale > 1 ? zoom.interacting ? 'grabbing' : 'grab' : 'zoom-in'
      }} data-id="element-94" data-mp="rl0a623f6m7x">
            <div className="absolute inset-0 flex items-center justify-center p-3 sm:p-6" data-id="element-95" data-mp="dmti52l5jgg6">
              <img ref={imageRef} key={current.src} src={current.src} alt={current.alt} draggable={false} className="max-h-full max-w-full rounded-[2px] object-contain" style={{
            transform: `translate3d(${zoom.offset.x}px, ${zoom.offset.y}px, 0) scale(${zoom.scale})`,
            transition: zoom.interacting ? 'none' : 'transform 180ms cubic-bezier(0.23, 1, 0.32, 1)'
          }} data-id="element-96" data-mp="33bcc1cgsj1b5" />
            </div>

            <button type="button" onClick={() => go(-1)} disabled={index === 0} aria-label="Précédent" onPointerDown={event => event.stopPropagation()} className={`${iconButton} absolute left-4 top-1/2 hidden -translate-y-1/2 md:flex`} data-id="element-97" data-mp="xajq97zzlfjh">
              <ChevronLeftIcon className="h-5 w-5" aria-hidden data-id="element-98" data-mp="1g8vzsuut8rfl" />
            </button>
            <button type="button" onClick={() => go(1)} disabled={index === count - 1} aria-label="Suivant" onPointerDown={event => event.stopPropagation()} className={`${iconButton} absolute right-4 top-1/2 hidden -translate-y-1/2 md:flex`} data-id="element-99" data-mp="1g6t0fk7rply7">
              <ChevronRightIcon className="h-5 w-5" aria-hidden data-id="element-100" data-mp="3ofpqh0rj6q8b" />
            </button>
          </div>

          <div className="shrink-0 border-t border-white/10 px-3 pb-2 pt-2 sm:px-6" data-id="element-101" data-mp="61qa09uyeip1">
            <div className="flex items-center justify-between gap-2" data-id="element-102" data-mp="20xy18up5zp2">
              <button type="button" onClick={() => go(-1)} disabled={index === 0} aria-label="Précédent" className={`${iconButton} md:invisible`} data-id="element-103" data-mp="3agztdl4poqjy">
                <ChevronLeftIcon className="h-5 w-5" aria-hidden data-id="element-104" data-mp="3jiqusds7yb80" />
              </button>

              <div className="flex items-center gap-2" data-id="element-105" data-mp="3eibizqnl5bs5">
                <button type="button" onClick={zoom.zoomOut} disabled={!zoom.canZoomOut} aria-label="Dézoomer" className={iconButton} data-id="element-106" data-mp="3ekxs3uq4jzch">
                  <MinusIcon className="h-5 w-5" aria-hidden data-id="element-107" data-mp="dujk7zof3fux" />
                </button>
                <span className="w-12 text-center font-heading text-[15px] font-semibold tabular-nums text-steel-pale" data-id="element-108" data-mp="8vzzu3ph2c4o">
                  {Math.round(zoom.scale * 100)}%
                </span>
                <button type="button" onClick={zoom.zoomIn} disabled={!zoom.canZoomIn} aria-label="Zoomer" className={iconButton} data-id="element-109" data-mp="4puzg902tko">
                  <PlusIcon className="h-5 w-5" aria-hidden data-id="element-110" data-mp="3m9tisxt3m2xy" />
                </button>
                <button type="button" onClick={zoom.reset} disabled={!zoom.canZoomOut} aria-label="Ajuster à l’écran" className={iconButton} data-id="element-111" data-mp="3hdcxebdaa2ok">
                  <ScanIcon className="h-5 w-5" aria-hidden data-id="element-112" data-mp="1bjd7a495h84a" />
                </button>
              </div>

              <button type="button" onClick={() => go(1)} disabled={index === count - 1} aria-label="Suivant" className={`${iconButton} md:invisible`} data-id="element-113" data-mp="2tgzqzidsajqh">
                <ChevronRightIcon className="h-5 w-5" aria-hidden data-id="element-114" data-mp="3382rahyw2uop" />
              </button>
            </div>
            <p className="mt-1.5 text-center text-[0.78rem] text-steel-light" data-id="element-115" data-mp="377v5pbayrv1z">
              Pincez, faites défiler ou touchez deux fois pour zoomer · faites glisser pour déplacer
            </p>
          </div>
        </motion.div>}
    </AnimatePresence>, document.body);
}